import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Switch,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import DataTable from '@/components/DataTable/DataTable';
import CpdActionButton from '@/components/ui/CpdActionButton';

import { useConfirmation } from '@/context/ConfirmationContext';
import { useDeleteCustomPaymentType } from '@/api/hooks/custom-payments/useDeleteCustomPaymentType';
import { useToggleCustomPaymentType } from '@/api/hooks/custom-payments/useToggleCustomPaymentType';

const CustomPaymentTypes = ({ organizationId }) => {
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { deleteCustomPaymentType } = useDeleteCustomPaymentType();
  const {
    toggleCustomPaymentType,
    loading: toggleLoading,
  } = useToggleCustomPaymentType();

  const handleToggleStatus = async (row) => {
    try {
      await toggleCustomPaymentType(
        row.organizationId || organizationId,
        row.id,
      );
      dataTableRef.current?.refresh();
      setSuccessMessage(
        `Custom payment type "${row.name}" status updated successfully.`,
      );
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update status.');
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 5000);
    }
  };

  const handleCreateCustomPaymentType = () => {
    if (organizationId) {
      navigate(
        `/admin/custom-payment-types/create?organizationId=${organizationId}`,
      );
    } else {
      navigate('/admin/custom-payment-types/create');
    }
  };

  const handleEditCustomPaymentType = (customPaymentType) => {
    if (organizationId) {
      navigate(
        `/admin/custom-payment-types/${customPaymentType.id}/edit?organizationId=${organizationId}`,
      );
    } else {
      navigate(`/admin/custom-payment-types/${customPaymentType.id}/edit`);
    }
  };

  const handleDeleteCustomPaymentType = (customPaymentType) => {
    const confirmMessage = `Are you sure you want to delete the custom payment type "${customPaymentType.name}"? This action cannot be undone.`;
    requestConfirmation(confirmMessage, () =>
      confirmDeleteCustomPaymentType(customPaymentType),
    );
  };

  const confirmDeleteCustomPaymentType = async (customPaymentType) => {
    try {
      await deleteCustomPaymentType(
        organizationId || customPaymentType.organizationId,
        customPaymentType.id,
      );

      dataTableRef.current?.refresh();

      setSuccessMessage(
        `Custom payment type "${customPaymentType.name}" has been successfully deleted.`,
      );
      setShowSuccessAlert(true);

      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 5000);
    } catch (error) {
      console.error('Error deleting custom payment type:', error);
      setErrorMessage(
        error.message ||
          'Failed to delete custom payment type. Please try again.',
      );
      setShowErrorAlert(true);

      setTimeout(() => {
        setShowErrorAlert(false);
      }, 5000);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Payment Name',
        render: (value) => (
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            {value}
          </Typography>
        ),
      },
      {
        key: 'entityType',
        label: 'Entity Type',
        render: (value) => {
          const formattedValue =
            value === 'INDIVIDUAL'
              ? 'Individual'
              : value === 'BUSINESS'
              ? 'Business'
              : value;
          return <Typography variant='body2'>{formattedValue}</Typography>;
        },
      },
      {
        key: 'identifiers',
        label: 'Identifiers',
        render: (value) => {
          const identifiers = value || [];
          if (identifiers.length === 0)
            return <Typography variant='body2'>-</Typography>;
          return (
            <Typography variant='body2'>{identifiers.join(', ')}</Typography>
          );
        },
      },
      {
        key: 'allowImmediatePayments',
        label: 'Immediate Payments',
        render: (value) => (
          <Typography variant='body2'>
            {value ? 'Allowed' : 'Not Allowed'}
          </Typography>
        ),
      },
      {
        key: 'enableEscheatment',
        label: 'Escheatment',
        render: (value) => (
          <Typography variant='body2'>
            {value ? 'Enabled' : 'Disabled'}
          </Typography>
        ),
      },
      {
        key: 'showInCreatePayment',
        label: 'Visible in Create Payment',
        render: (value) => (
          <Typography variant='body2'>{value ? 'Yes' : 'No'}</Typography>
        ),
      },
      {
        key: 'isDisabled',
        label: 'Status',
        render: (value, row) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch
              checked={value !== true}
              onChange={() => handleToggleStatus(row)}
              color='primary'
              size='small'
              disabled={toggleLoading}
            />
            <Typography variant='caption' sx={{ ml: 1 }}>
              {!value ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title='Edit'>
              <IconButton
                size='small'
                onClick={() => handleEditCustomPaymentType(row)}
                color='primary'>
                <Edit fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton
                size='small'
                onClick={() => handleDeleteCustomPaymentType(row)}
                color='error'>
                <Delete fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleToggleStatus, toggleLoading],
  );

  const content = (
    <Box sx={{ p: organizationId ? 0 : 3 }}>
      {showSuccessAlert && (
        <Alert
          severity='success'
          onClose={() => setShowSuccessAlert(false)}
          sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {showErrorAlert && (
        <Alert
          severity='error'
          onClose={() => setShowErrorAlert(false)}
          sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}>
        {!organizationId && (
          <CardHeader color='primary'>
            <h4 style={{ margin: 0 }}>Custom Payment Types</h4>
          </CardHeader>
        )}

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <CpdActionButton
            variant='contained'
            color='primary'
            startIcon={<Add sx={{ color: 'white' }} />}
            onClick={handleCreateCustomPaymentType}>
            Create Custom Payment Type
          </CpdActionButton>
        </Box>
      </Box>

      <DataTable
        ref={dataTableRef}
        columns={columns}
        endpoint='/custom-payment-types'
        pageSize={1000}
        initialSort={{ key: 'name', order: 'asc' }}
        initialFilters={organizationId ? { organizationId } : {}}
      />
    </Box>
  );

  if (organizationId) {
    return content;
  }

  return <PageLayout>{content}</PageLayout>;
};

export default CustomPaymentTypes;
